<template>
    <div class="flex mt-[42px] mb-[27px] justify-between h-[42px]">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.reports_header") }}
        </h1>
    </div>

    <BasicTabs :tabs="tabs" :tab-key="'notifications'" class="mt-[21px]" />

    <!-- Upgrade Required Section -->
    <div v-if="!hasReportsAccess">
        <InfoCard
            :img-src="lockedNotAuthorized"
            img-alt="User has no access to content"
            :title="t('vue.upgrade_required')"
            :body-text="t('vue.not_having_access_message')"
            :show-dot="false"
            :button-label="t('vue.upgrade_my_plan')"
            :button-href="route('billing.subscriptions.index')"
            :disable-top-margin="false"
        />
    </div>

    <div v-else-if="totalDomains === 0" class="mt-[42px]">
        <InfoCard
            :img-src="searchSvg"
            img-alt="Reports none image"
            :title="t('vue.none_reports_yet')"
            :body-text="t('vue.none_reports_yet_instruction')"
            :show-dot="false"
            :disable-top-margin="true"
        >
            <template #actions>
                <AtomicButton
                    color="yellow"
                    size="md"
                    class="h-8 px-4 text-[12px] font-semibold leading-4"
                    @click="handleAddDomain"
                >
                    {{ t("vue.add_new_domain") }}
                </AtomicButton>
            </template>
        </InfoCard>
    </div>

    <!-- Main Content -->
    <div v-else class="mb-[98px]">
        <div class="mt-[44px] grid-cols-layout pb-5 lg:grid">
            <div class="mb-6 lg:mb-0 lg:mt-4">
                <h3
                    class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                >
                    {{ $t("vue.notification_types") }}
                </h3>
                <p class="max-w-xs text-sm text-gray-500">
                    {{ $t("vue.notification_types_desc") }}
                </p>
            </div>

            <div class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0">
                <div class="p-6">
                    <div class="flex items-center justify-between mb-4">
                        <div>
                            <h4 class="text-base font-medium text-gray-900">
                                {{ $t("vue.weekly_overall_reports") }}
                            </h4>
                            <p class="text-sm text-gray-500">
                                {{ $t("vue.weekly_overall_reports_desc") }}
                            </p>
                        </div>
                        <Switch v-model="weeklyReport" />
                    </div>

                    <div class="flex items-center justify-between">
                        <div>
                            <h4 class="text-base font-medium text-gray-900">
                                {{ $t("vue.monthly_overall_reports") }}
                            </h4>
                            <p class="text-sm text-gray-500">
                                {{ $t("vue.monthly_overall_reports_desc") }}
                            </p>
                        </div>
                        <Switch v-model="monthlyReport" />
                    </div>
                </div>

                <div class="flex justify-end border-t border-gray-200">
                    <div class="my-3 pr-6">
                        <AtomicButton
                            type="button"
                            color="yellow"
                            size="sm"
                            :disabled="!hasChanges || loading"
                            @click="saveSettings"
                        >
                            {{ $t("vue.save_changes") }}
                        </AtomicButton>
                    </div>
                </div>
            </div>
        </div>

        <!-- Email Notifications Section -->
        <div class="mt-5 grid-cols-layout pb-5 lg:grid">
            <div class="mb-6 lg:mb-0 lg:mt-4">
                <h3
                    class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                >
                    {{ $t("vue.email_notifications") }}
                </h3>
                <p class="max-w-xs text-sm text-gray-500">
                    {{ $t("vue.email_notifications_desc") }}
                </p>
            </div>

            <div class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0">
                <div class="p-6">
                    <div class="space-y-4">
                        <!-- Existing emails list -->
                        <div
                            v-if="notificationEmails.length > 0"
                            class="space-y-3"
                        >
                            <div
                                v-for="(email, index) in notificationEmails"
                                :key="index"
                                class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                                <div class="flex items-center space-x-3">
                                    <div
                                        :class="[
                                            'flex h-8 w-8 items-center justify-center rounded-full',
                                            getRecipientIconColor(
                                                recipients.find(
                                                    (r) => r.email === email,
                                                ),
                                            ),
                                        ]"
                                    >
                                        <Mail class="h-4 w-4 text-white" />
                                    </div>
                                    <div class="flex flex-col">
                                        <span
                                            class="text-sm font-medium text-gray-900"
                                        >
                                            {{ email }}
                                        </span>
                                        <span class="text-xs text-gray-500">
                                            {{
                                                getRecipientLabel(
                                                    recipients.find(
                                                        (r) =>
                                                            r.email === email,
                                                    ),
                                                )
                                            }}
                                        </span>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    class="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    :disabled="loading"
                                    @click="removeEmail(index)"
                                >
                                    <X class="h-4 w-4" />
                                </Button>
                            </div>
                        </div>

                        <!-- Add new email form -->
                        <div class="flex items-center space-x-3">
                            <div class="flex-1">
                                <TextInput
                                    v-model="newEmail"
                                    type="email"
                                    :placeholder="t('vue.enter_email_address')"
                                    :error="emailError"
                                    :disabled="!weeklyReport && !monthlyReport"
                                    @keydown.enter="addEmail"
                                />
                            </div>
                            <Button
                                :disabled="
                                    !newEmail.trim() ||
                                    notificationEmails.length >= 5 ||
                                    loading ||
                                    (!weeklyReport && !monthlyReport)
                                "
                                class="shrink-0"
                                @click="addEmail"
                            >
                                <Plus class="h-4 w-4 mr-2" />
                                {{ $t("vue.add_email") }}
                            </Button>
                        </div>

                        <!-- Notification disabled message -->
                        <div
                            v-if="!weeklyReport && !monthlyReport"
                            class="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200"
                        >
                            <div class="flex items-center space-x-2">
                                <svg
                                    class="h-4 w-4 text-amber-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                <span>
                                    {{
                                        $t(
                                            "vue.enable_at_lease_one_notification",
                                        )
                                    }}
                                </span>
                            </div>
                        </div>

                        <!-- Email limit indicator -->
                        <div
                            class="flex items-center justify-between text-xs text-gray-500"
                        >
                            <span>
                                {{ notificationEmails.length }} / 5
                                {{ $t("vue.emails_added_reports_add_email") }}
                            </span>
                            <span
                                v-if="notificationEmails.length >= 5"
                                class="text-amber-600"
                            >
                                Maximum limit reached
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import UpgradeRequired from "@/components/Atomic/Miscellaneous/UpgradeRequired.vue";
import useReportsAccess from "@/composables/useReportsAccess";
import Switch from "@/components/Atomic/Switch/Switch.vue";
import TextInput from "@/components/Atomic/Inputs/TextInput.vue";
import Button from "@/components/ui/button/Button.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import {
    Globe,
    Settings,
    Mail,
    Plus,
    X,
    AlertOctagon,
    Bell,
} from "lucide-vue-next";
import { ref, computed, onMounted, defineProps, withDefaults } from "vue";
import type TabItem from "@/types/Settings/TabItem";
import { create } from "@/composables/useNotifications";
import axios from "axios";
import { ChartBarIcon } from "@heroicons/vue/24/outline";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();
const { hasReportsAccess } = useReportsAccess();

const lockedNotAuthorized = new URL(
    "/resources/js/assets/images/locked-not-authorized.svg",
    import.meta.url,
).href;

const searchSvg = new URL(
    "/resources/js/assets/images/search.svg",
    import.meta.url,
).href;

// Type definitions for notifications
interface ReportNotification {
    id: number;
    name: string;
    description: string;
    frequency: "weekly" | "monthly" | "daily" | "quarterly" | "yearly";
    is_active: boolean;
    team_id: number;
    created_by: number;
    last_sent_at: string | null;
    next_send_at: string | null;
    settings: Record<string, unknown>;
    created_at: string;
    updated_at: string;
}

// Define props
interface Props {
    notifications?: ReportNotification[];
    totalDomains: number;
}

const props = withDefaults(defineProps<Props>(), {
    notifications: () => [],
});

// State for notification settings
const weeklyReport = ref(false);
const monthlyReport = ref(false);
const loading = ref(false);

// Track initial values to detect changes
const initialWeeklyReport = ref(false);
const initialMonthlyReport = ref(false);

// Track notification IDs for toggling
const weeklyNotificationId = ref<number | null>(null);
const monthlyNotificationId = ref<number | null>(null);

// Email notifications state
const notificationEmails = ref<string[]>([]);
const newEmail = ref("");
const emailError = ref("");

// Recipients data for storing user info
const recipients = ref<
    Array<{
        email: string;
        user_id?: number;
        is_external: boolean;
        is_team_member?: boolean;
    }>
>([]);

// Computed property to check if there are changes
const hasChanges = computed(() => {
    return (
        weeklyReport.value !== initialWeeklyReport.value ||
        monthlyReport.value !== initialMonthlyReport.value
    );
});

// Tabs configuration
const tabs: TabItem[] = [
    {
        title: t("vue.overview"),
        key: "domain-overview",
        icon: ChartBarIcon,
        url: "/reports",
    },
    {
        title: t("vue.notifications"),
        key: "notifications",
        icon: Bell,
        url: "/reports/notifications",
    },
];

// Save settings function
const saveSettings = async () => {
    loading.value = true;

    try {
        // First, ensure default notifications exist (this will create or reactivate them)
        const createResponse = await axios.post(
            "/reports/settings/create-default-notifications",
        );

        // Extract notification IDs from response
        const { notifications } = createResponse.data;
        const weeklyNotification = notifications.weekly;
        const monthlyNotification = notifications.monthly;

        // Store IDs for future use
        weeklyNotificationId.value = weeklyNotification.id;
        monthlyNotificationId.value = monthlyNotification.id;

        // Now toggle notifications based on switch states
        // If the notification is active but switch is off, disable it
        // If the notification is inactive but switch is on, enable it

        if (weeklyNotification.is_active !== weeklyReport.value) {
            await axios.patch(
                `/reports/settings/notifications/${weeklyNotification.id}/toggle`,
            );
        }

        if (monthlyNotification.is_active !== monthlyReport.value) {
            await axios.patch(
                `/reports/settings/notifications/${monthlyNotification.id}/toggle`,
            );
        }

        // Update initial values to match current state
        initialWeeklyReport.value = weeklyReport.value;
        initialMonthlyReport.value = monthlyReport.value;

        // Refresh recipients list to show any newly added default recipients
        await loadRecipients();

        create({
            title: "Settings Updated",
            text: "Your notification preferences have been saved successfully.",
            type: "success",
        });
    } catch (error) {
        console.error("Failed to save settings:", error);
        create({
            title: "Save Failed",
            text: "There was an error saving your settings. Please try again.",
            type: "error",
        });
    } finally {
        loading.value = false;
    }
};

// Recipient helper functions
const getRecipientIconColor = (recipient?: {
    email: string;
    user_id?: number;
    is_external: boolean;
    is_team_member?: boolean;
}): string => {
    if (!recipient) return "bg-gray-500";

    if (recipient.is_external) {
        return "bg-orange-500"; // External recipient
    } else if (recipient.is_team_member) {
        return "bg-blue-500"; // Team member
    } else {
        return "bg-purple-500"; // Registered user (exists in DB but not team member)
    }
};

const getRecipientLabel = (recipient?: {
    email: string;
    user_id?: number;
    is_external: boolean;
    is_team_member?: boolean;
}): string => {
    if (!recipient) return "Unknown";

    if (recipient.is_external) {
        return "External recipient";
    } else if (recipient.is_team_member) {
        return "Team member";
    } else {
        return "Registered user";
    }
};

// Email management functions
const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const addEmail = async () => {
    emailError.value = "";

    // Prevent adding emails when no notifications are enabled
    if (!weeklyReport.value && !monthlyReport.value) {
        emailError.value = "Please enable at least one notification type first";
        return;
    }

    const email = newEmail.value.trim();

    if (!email) {
        emailError.value = "Please enter an email address";
        return;
    }

    if (!validateEmail(email)) {
        emailError.value = "Please enter a valid email address";
        return;
    }

    if (notificationEmails.value.includes(email)) {
        emailError.value = "This email address is already added";
        return;
    }

    if (notificationEmails.value.length >= 5) {
        emailError.value = "Maximum of 5 email addresses allowed";
        return;
    }

    try {
        loading.value = true;
        const response = await axios.post("/report-notifications/recipients", {
            email: email,
        });

        // Add to local arrays
        notificationEmails.value.push(email);
        recipients.value.push(response.data.recipient);
        newEmail.value = "";

        create({
            title: "Recipient Added",
            text: `${email} has been added as a recipient.`,
            type: "success",
        });
    } catch (error) {
        console.error("Failed to add recipient:", error);
        create({
            title: "Failed to Add Recipient",
            text: "There was an error adding the recipient. Please try again.",
            type: "error",
        });
    } finally {
        loading.value = false;
    }
};

const removeEmail = async (index: number) => {
    const emailToRemove = notificationEmails.value[index];

    if (!emailToRemove) return;

    try {
        loading.value = true;
        await axios.delete("/report-notifications/recipients", {
            data: { email: emailToRemove },
        });

        // Remove from local arrays
        notificationEmails.value.splice(index, 1);
        const recipientIndex = recipients.value.findIndex(
            (r: {
                email: string;
                user_id?: number;
                is_external: boolean;
                is_team_member?: boolean;
            }) => r.email === emailToRemove,
        );
        if (recipientIndex !== -1) {
            recipients.value.splice(recipientIndex, 1);
        }

        create({
            title: "Recipient Removed",
            text: `${emailToRemove} has been removed as a recipient.`,
            type: "success",
        });
    } catch (error) {
        console.error("Failed to remove recipient:", error);
        create({
            title: "Failed to Remove Recipient",
            text: "There was an error removing the recipient. Please try again.",
            type: "error",
        });
    } finally {
        loading.value = false;
    }
};

// Load existing recipients
const loadRecipients = async () => {
    try {
        const response = await axios.get("/report-notifications/recipients");
        recipients.value = response.data.recipients || [];
        notificationEmails.value = recipients.value.map(
            (r: {
                email: string;
                user_id?: number;
                is_external: boolean;
                is_team_member?: boolean;
            }) => r.email,
        );
    } catch (error) {
        console.error("Failed to load recipients:", error);
    }
};

// Load existing notifications
const loadNotifications = () => {
    try {
        // The notifications are passed as props from the controller
        const notifications = props.notifications || [];

        // Find weekly and monthly notifications
        const weeklyNotification = notifications.find(
            (n: ReportNotification) => n.frequency === "weekly",
        );
        const monthlyNotification = notifications.find(
            (n: ReportNotification) => n.frequency === "monthly",
        );

        if (weeklyNotification) {
            weeklyReport.value = weeklyNotification.is_active;
            weeklyNotificationId.value = weeklyNotification.id;
        }

        if (monthlyNotification) {
            monthlyReport.value = monthlyNotification.is_active;
            monthlyNotificationId.value = monthlyNotification.id;
        }

        // Set initial values for change detection
        initialWeeklyReport.value = weeklyReport.value;
        initialMonthlyReport.value = monthlyReport.value;
    } catch (error) {
        console.error("Failed to load notifications:", error);
    }
};

// Initialize settings
onMounted(() => {
    loadNotifications();
    loadRecipients();
});
</script>
